import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Badge, EmptyState, IconButton, Page, PageHeader, Segmented, Switch, TableSkeleton, TagInput } from "../../../components/ui";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";
import { apiError, getAppConfig, getUpdatePushes, sendUpdatePush, updateAppConfig } from "../../../services/mobileApp";
import { can } from "../../../utils/roles";
import "./mobile-app.css";

const formatWhen = (date) =>
  date ? new Date(date).toLocaleString([], { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const PLATFORMS = [
  { value: "all", label: "Android and iOS" },
  { value: "android", label: "Android" },
  { value: "ios", label: "iOS" },
];
const PLATFORM_LABELS = { all: "Android and iOS", android: "Android", ios: "iOS" };
const STATUS = { sent: { label: "Sent", color: "green" }, failed: { label: "Failed", color: "red" }, sending: { label: "Sending", color: "blue" } };

/** One editable field of a Firestore document: switch, list or text, saved on its own. */
const ConfigField = ({ field, disabled, onSave }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(field.value);
  useEffect(() => setDraft(field.value), [field.value]);
  const changed = JSON.stringify(draft) !== JSON.stringify(field.value);

  let control;
  if (field.type === "boolean") {
    control = (
      <Switch checked={Boolean(field.value)} label={t(field.label)} onLabel={t("Yes")} offLabel={t("No")} onChange={(value) => !disabled && onSave(field, value)} />
    );
  } else if (field.type === "list") {
    control = <TagInput id={`cfg-${field.key}`} value={draft || []} disabled={disabled} onChange={setDraft} placeholder={t("Type then press Enter")} />;
  } else if (field.type === "string" || field.type === "number") {
    control = (
      <input
        id={`cfg-${field.key}`}
        className="ui-input"
        type={field.type === "number" ? "number" : "text"}
        value={draft ?? ""}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
      />
    );
  } else {
    control = <code className="mobile-app-raw">{JSON.stringify(field.value)}</code>;
  }

  return (
    <div className="mobile-app-field" data-type={field.type}>
      <div className="mobile-app-field-text">
        <label htmlFor={`cfg-${field.key}`}>{t(field.label)}</label>
        <code>{field.key}</code>
      </div>
      <div className="mobile-app-field-control">
        {control}
        {field.type !== "boolean" && !disabled && changed && (
          <div className="mobile-app-field-actions">
            <button type="button" className="ui-btn" onClick={() => onSave(field, draft)}>
              {t("Save")}
            </button>
            <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setDraft(field.value)}>
              {t("Cancel")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/** Live configuration of the app, read from Firestore on every load. */
const ConfigSection = ({ canEdit }) => {
  const { t } = useTranslation();
  const [config, setConfig] = useState(null);
  const [pending, setPending] = useState(null); // { doc, field, value }

  const load = useCallback(() => getAppConfig().then(setConfig), []);
  useEffect(() => {
    load();
  }, [load]);

  const save = async ({ doc, field, value }) => {
    try {
      const next = await updateAppConfig(doc.path, { [field.key]: value });
      setConfig(next);
      toast.success(t("Saved: the app applies it right away"));
    } catch (error) {
      toast.error(apiError(error) || t("Something went wrong"));
    }
  };

  if (!config) return <TableSkeleton rows={6} />;
  return (
    <>
      <div className="mobile-app-toolbar">
        <p className="ui-hint">{t("Read live from Firestore. A change applies to every installed app, without a new version.")}</p>
        <IconButton icon="ri-refresh-line" label={t("Refresh")} onClick={load} />
      </div>
      {config.docs.map((doc) => (
        <section key={doc.path} className="ui-card mobile-app-doc">
          <header>
            <h2>{t(doc.label)}</h2>
            <span className="ui-media-meta">
              <code>{doc.path}</code> · {t("Updated")} {formatWhen(doc.updatedAt)}
            </span>
          </header>
          {!doc.exists ? (
            <EmptyState icon="ri-database-2-line">{t("This document does not exist in Firestore")}</EmptyState>
          ) : (
            doc.fields.map((field) => (
              <ConfigField key={field.key} field={field} disabled={!canEdit} onSave={(f, value) => setPending({ doc, field: f, value })} />
            ))
          )}
        </section>
      ))}
      <ConfirmDialog
        open={Boolean(pending)}
        onClose={() => setPending(null)}
        onConfirm={() => save(pending)}
        danger={pending?.field.key === "maintenance" && pending?.value === true}
        confirmLabel={t("Apply")}
      >
        <strong>{pending && t(pending.field.label)}</strong>
        <p>
          {pending?.field.type === "boolean"
            ? pending.value
              ? t("Turn on?")
              : t("Turn off?")
            : `${t("New value")} : ${Array.isArray(pending?.value) ? pending.value.join(", ") : pending?.value}`}
        </p>
        <p>{t("The change applies right away to every user of the app.")}</p>
      </ConfirmDialog>
    </>
  );
};

/** What the user sees: the notification, then the update window of the app (brand colors). */
const UpdatePreview = ({ title, body, version, platform }) => {
  const { t } = useTranslation();
  return (
    <div className="mobile-app-preview" aria-label={t("Preview")}>
      <div className="mobile-app-push">
        <span className="mobile-app-push-icon" aria-hidden="true">AFO</span>
        <div>
          <strong>{title}</strong>
          <p>{body}</p>
        </div>
      </div>
      <i className="ri-arrow-down-line mobile-app-preview-arrow" aria-hidden="true"></i>
      <div className="mobile-app-sheet">
        <span className="mobile-app-sheet-handle" aria-hidden="true" />
        <span className="mobile-app-sheet-icon" aria-hidden="true">
          <i className="ri-download-cloud-line"></i>
        </span>
        <strong>{t("Update available")}</strong>
        <p>{t("A new version of AFO MEDIA is available, with new features and improvements.")}</p>
        <dl>
          <div>
            <dt>{t("Installed version")}</dt>
            <dd>{t("on the phone")}</dd>
          </div>
          <div>
            <dt>{t("Latest version")}</dt>
            <dd className="mobile-app-sheet-new">
              {version || "—"} <span>{t("NEW")}</span>
            </dd>
          </div>
        </dl>
        <span className="mobile-app-sheet-button">
          {platform === "ios" ? "App Store" : platform === "android" ? "Google Play" : t("Update")}
        </span>
        <span className="mobile-app-sheet-later">{t("Later")}</span>
      </div>
    </div>
  );
};

/** "Update available" notification to Android, iOS or both, and its history. */
const UpdateSection = ({ canEdit }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [platform, setPlatform] = useState("all");
  const [version, setVersion] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(
    () =>
      getUpdatePushes().then((result) => {
        setData(result);
        if (result?.defaults) {
          setTitle((current) => current || result.defaults.title);
          setBody((current) => current || result.defaults.body);
        }
      }),
    []
  );
  useEffect(() => {
    load();
  }, [load]);

  const send = async () => {
    setSending(true);
    try {
      await sendUpdatePush({ platform, title: title.trim(), body: body.trim(), latestVersion: version.trim() });
      toast.success(t("Update notification sent"));
      load();
    } catch (error) {
      toast.error(apiError(error) || t("Something went wrong"));
    } finally {
      setSending(false);
    }
  };

  const ready = title.trim() && body.trim();
  return (
    <>
      <section className="ui-card mobile-app-update">
        <div className="mobile-app-form">
          {canEdit ? (
            <>
              <span className="ui-label">{t("Send to")}</span>
              <Segmented options={PLATFORMS.map((p) => ({ ...p, label: t(p.label) }))} value={platform} onChange={setPlatform} label={t("Send to")} />
              <label className="ui-label" htmlFor="update-version">
                {t("Version number shown in the app (optional)")}
              </label>
              <input id="update-version" className="ui-input" value={version} placeholder="3.3.0" onChange={(e) => setVersion(e.target.value)} />
              <label className="ui-label" htmlFor="update-title">
                {t("Title")}
              </label>
              <input id="update-title" className="ui-input" value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} />
              <label className="ui-label" htmlFor="update-body">
                {t("Text")}
              </label>
              <textarea id="update-body" className="ui-input" rows={4} value={body} maxLength={400} onChange={(e) => setBody(e.target.value)} />
              <p className="ui-hint">
                {t("On tap, the app opens its update window, with a button to the store of the phone. Phones of the other system receive nothing visible.")}
              </p>
              <button type="button" className="ui-btn" disabled={!ready || sending} onClick={() => setConfirm(true)}>
                <i className="ri-send-plane-line" aria-hidden="true"></i>
                {sending ? t("Sending…") : t("Send the notification")}
              </button>
            </>
          ) : (
            <p className="ui-hint">{t("You can see the history; sending requires full access to this section.")}</p>
          )}
        </div>
        <UpdatePreview title={title} body={body} version={version} platform={platform} />
      </section>

      <section className="ui-card">
        <h2 className="mobile-app-h2">{t("Notifications sent")}</h2>
        {!data ? (
          <TableSkeleton rows={3} />
        ) : !data.items.length ? (
          <EmptyState icon="ri-notification-off-line">{t("No update notification sent yet")}</EmptyState>
        ) : (
          <div className="ui-table-wrap">
            <table className="ui-table">
              <thead>
                <tr>
                  <th>{t("Date")}</th>
                  <th>{t("Systems")}</th>
                  <th>{t("Notification")}</th>
                  <th>{t("status")}</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr key={item._id}>
                    <td className="ui-cell-muted">{formatWhen(item.sentAt || item.createdAt)}</td>
                    <td>{t(PLATFORM_LABELS[item.platform] || "Android and iOS")}</td>
                    <td>
                      <span className="ui-media-title">{item.title}</span>
                      <span className="ui-media-meta">{item.body}</span>
                    </td>
                    <td>
                      <Badge color={(STATUS[item.status] || STATUS.sending).color} dot>
                        {t((STATUS[item.status] || STATUS.sending).label)}
                      </Badge>
                      {item.error && <span className="ui-media-meta">{item.error}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <ConfirmDialog open={confirm} onClose={() => setConfirm(false)} onConfirm={send} confirmLabel={t("Send")}>
        <strong>
          {t("Send the update notification to")} {t(PLATFORM_LABELS[platform])} ?
        </strong>
        <p>{t("Every device of this system receives it right away.")}</p>
      </ConfirmDialog>
    </>
  );
};

/**
 * Mobile app: its live configuration (Firestore) and the "Update available"
 * notification, to Android, iOS or both.
 */
const MobileApp = () => {
  const { t } = useTranslation();
  const canEdit = can("mobile_app", "edit");
  const [tab, setTab] = useState("config");

  return (
    <Page>
      <PageHeader title={t("Mobile app")} subtitle={t("Configuration of the app (Firestore) and update notification to Android, iOS or both.")} />
      <div className="mobile-app-tabs">
        <Segmented
          label={t("Section")}
          value={tab}
          onChange={setTab}
          options={[
            { value: "config", label: t("Configuration") },
            { value: "update", label: t("Update notification") },
          ]}
        />
      </div>
      {tab === "config" ? <ConfigSection canEdit={canEdit} /> : <UpdateSection canEdit={canEdit} />}
    </Page>
  );
};

export default MobileApp;
